import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import StatsCard from "@/components/dashboard/StatsCard";
import {
  LineChart,
  Line,
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Users,
  DollarSign,
  Gift,
  UserCheck,
  AlertCircle,
  TrendingUp,
  Award,
  Star,
} from "lucide-react";
import {
  fetchReferralCollaborationAnalytics,
  ReferralCollaborationAnalytics,
  DateRange,
} from "@/api/business-analytics";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

interface ReferralCollaborationTabProps {
  dateRange: DateRange;
}

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];

// Custom tooltip component that supports dark mode
interface TooltipPayload {
  color: string;
  name: string;
  value: string | number;
}

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-background border border-border rounded-lg shadow-lg p-3">
        <p className="font-medium text-foreground">{label}</p>
        {payload.map((entry: TooltipPayload, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const ReferralCollaborationTab: React.FC<
  ReferralCollaborationTabProps
> = ({ dateRange }) => {
  const {
    data: analytics,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: [
      "referralCollabAnalytics",
      dateRange.from.toISOString(),
      dateRange.to.toISOString(),
    ],
    queryFn: () => fetchReferralCollaborationAnalytics(dateRange),
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  // Hooks must be unconditionally invoked; compute derived values with safe defaults
  const totalEarnings = useMemo(
    () =>
      analytics
        ? analytics.collaboratorCommissions + analytics.totalReferralRewards
        : 0,
    [analytics]
  );
  const avgCollaboratorEarning = useMemo(
    () =>
      analytics && analytics.totalCollaborators > 0
        ? analytics.collaboratorCommissions / analytics.totalCollaborators
        : 0,
    [analytics]
  );

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
              <div className="h-4 w-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load referral collaboration analytics. Please try again.
        </AlertDescription>
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No referral or collaboration data available for the selected date
          range.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Collaborators"
          value={analytics.totalCollaborators}
          icon={<Users className="h-4 w-4" />}
          trend={{
            value: analytics.totalCollaborators > 0 ? 15 : 0,
            isPositive: analytics.totalCollaborators > 0,
            period: "prev. period",
          }}
        />
        <StatsCard
          title="Collaborator Earnings"
          value={`₹${analytics.collaboratorCommissions.toFixed(2)}`}
          icon={<DollarSign className="h-4 w-4" />}
          trend={{
            value: 12.5,
            isPositive: true,
            period: "prev. period",
          }}
        />
        <StatsCard
          title="Total Referrals"
          value={analytics.totalReferrals}
          icon={<UserCheck className="h-4 w-4" />}
          trend={{
            value: analytics.totalReferrals > 0 ? 20 : 0,
            isPositive: analytics.totalReferrals > 0,
            period: "prev. period",
          }}
        />
        <StatsCard
          title="Referral Rewards"
          value={`₹${analytics.totalReferralRewards.toFixed(2)}`}
          icon={<Gift className="h-4 w-4" />}
          trend={{
            value: 8.3,
            isPositive: true,
            period: "prev. period",
          }}
        />
      </div>

      {/* Referral Trends & Collaborator Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Referral Program Trends</CardTitle>
            <CardDescription>
              Monthly referral activity and reward distribution
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer
              width="100%"
              height={350}
              className="[&_.recharts-line]:hover:brightness-110 [&_.recharts-line]:transition-all [&_.recharts-line]:duration-200"
            >
              <LineChart data={analytics.referralTrends}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="month" className="text-muted-foreground" />
                <YAxis className="text-muted-foreground" />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="newReferrals"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  name="New Referrals"
                  className="hover:brightness-110"
                />
                <Line
                  type="monotone"
                  dataKey="rewardsGranted"
                  stroke="#10b981"
                  strokeWidth={2}
                  name="Rewards Granted"
                  className="hover:brightness-110"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top Collaborator Performance</CardTitle>
            <CardDescription>Leading collaborators by earnings</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.collaboratorPerformance.length > 0 ? (
                analytics.collaboratorPerformance
                  .slice(0, 5)
                  .map((collaborator, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors duration-200"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                          <Star className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h4 className="font-medium">{collaborator.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            {collaborator.promotions} promotions
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">
                          ₹{collaborator.earnings.toFixed(2)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {collaborator.conversions} conversions
                        </p>
                      </div>
                    </div>
                  ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No collaborator data found for the selected period</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Financial Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Financial Overview</CardTitle>
            <CardDescription>
              Collaboration and referral economics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                Total Referral Discounts
              </span>
              <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
                ₹{analytics.referralDiscounts.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                Pending Payouts
              </span>
              <span className="text-sm font-medium text-red-600 dark:text-red-400">
                ₹{analytics.pendingPayouts.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                Avg Collaborator Earning
              </span>
              <span className="text-sm font-medium text-green-600 dark:text-green-400">
                ₹{avgCollaboratorEarning.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                Total Program Cost
              </span>
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                ₹{totalEarnings.toFixed(2)}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Monthly Referral Growth</CardTitle>
            <CardDescription>
              Referral activity and reward trends over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer
              width="100%"
              height={300}
              className="[&_.recharts-bar]:hover:brightness-110 [&_.recharts-bar]:hover:cursor-pointer [&_.recharts-bar]:transition-all [&_.recharts-bar]:duration-200"
            >
              <RechartsBarChart data={analytics.referralTrends}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="month" className="text-muted-foreground" />
                <YAxis className="text-muted-foreground" />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="newReferrals"
                  fill="#3b82f6"
                  name="New Referrals"
                  className="hover:brightness-110 transition-all duration-200 cursor-pointer"
                />
                <Bar
                  dataKey="rewardsGranted"
                  fill="#10b981"
                  name="Rewards Granted"
                  className="hover:brightness-110 transition-all duration-200 cursor-pointer"
                />
              </RechartsBarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Active Promotions */}
      <Card>
        <CardHeader>
          <CardTitle>Active Promotions</CardTitle>
          <CardDescription>
            Current promotion campaigns and their performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {analytics.activePromotions.length > 0 ? (
              analytics.activePromotions.map((promotion, index) => (
                <div
                  key={index}
                  className="p-4 border rounded-lg hover:bg-muted/50 transition-colors duration-200"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{promotion.name}</h4>
                    <Badge
                      variant={promotion.is_active ? "default" : "secondary"}
                    >
                      {promotion.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {promotion.description || "No description"}
                  </p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Code:</span>
                      <span className="font-mono">{promotion.code}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Discount:</span>
                      <span>
                        {promotion.discount_value}
                        {promotion.discount_type === "percentage" ? "%" : " ₹"}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Used:</span>
                      <span>
                        {promotion.current_uses}
                        {promotion.max_uses ? `/${promotion.max_uses}` : ""}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-8 text-muted-foreground">
                <Award className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No active promotions found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Program Performance Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Program Performance Summary</CardTitle>
          <CardDescription>
            Overall collaboration and referral program effectiveness
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200">
                Active Collaborators
              </h3>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {analytics.totalCollaborators}
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                Contributing partners
              </p>
            </div>
            <div className="text-center p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-200">
                Total Earnings
              </h3>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                ₹{analytics.collaboratorCommissions.toFixed(0)}
              </p>
              <p className="text-sm text-green-600 dark:text-green-400">
                Paid to collaborators
              </p>
            </div>
            <div className="text-center p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-200">
                Referral Success
              </h3>
              <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                {analytics.totalReferrals}
              </p>
              <p className="text-sm text-purple-600 dark:text-purple-400">
                Successful referrals
              </p>
            </div>
            <div className="text-center p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-200">
                Program ROI
              </h3>
              <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                {analytics.referralDiscounts > 0
                  ? (
                      (analytics.referralDiscounts / totalEarnings) *
                      100
                    ).toFixed(0)
                  : 0}
                %
              </p>
              <p className="text-sm text-orange-600 dark:text-orange-400">
                Return on investment
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
